import { TuhiUserDto } from 'app/models/generated'

// Serializable interface for Redux storage
export interface TuhiUserData {
    id: string // id is mapped from cpn
    sub: string
    confidenceLevel: number
    firstName: string
    lastName: string
    email?: string | null
    birthDate?: string | null
    // ACA-102: org-admin role model. `role` gates admin access
    // (`admin` | `clinician`, default `clinician`); `status` marks
    // (de)activation (`active` | `inactive`). Optional here because the
    // fields are additive on TuhiUserDto and may be absent until the backend
    // schema is deployed and codegen regenerated.
    role?: string | null
    status?: string | null
    // ACA-159/ACA-164: clinician profession + specialty (specialty by code).
    // Optional/additive — read defensively until codegen catches up.
    profession?: string | null
    specialty?: string | null
    // ACA-266: user-managed preferred contact details, used to prefill forms.
    // Optional/additive — read defensively until codegen catches up.
    preferredPhone?: string | null
    preferredEmail?: string | null
    preferredAddress?: string | null
    subscription?: TuhiSubscriptionData | null
}

// Serializable subset of SubscriptionDto kept in Redux. `endDate` and
// `status` drive the plan screen's cancel/resume section (status
// `CANCELLED` after a cancel, access until `endDate`).
export interface TuhiSubscriptionData {
    id: string
    type: string
    monthlyConsultLimit: number
    consultsUsedThisMonth: number
    description: string
    endDate?: string | null
    status?: string | null
}

/**
 * Copy the fields Redux keeps from a SubscriptionDto (or a GraphQL result of
 * the same shape). Every path that stores a subscription goes through this,
 * so none of them can drop `endDate`/`status` again.
 */
export const toSubscriptionData = (subscription: {
    id: string
    type: string
    monthlyConsultLimit: number
    consultsUsedThisMonth: number
    description: string
    endDate?: string | null
    status?: string | null
}): TuhiSubscriptionData => ({
    id: subscription.id,
    type: subscription.type,
    monthlyConsultLimit: subscription.monthlyConsultLimit,
    consultsUsedThisMonth: subscription.consultsUsedThisMonth,
    description: subscription.description,
    endDate: subscription.endDate ?? null,
    status: subscription.status ?? null,
})

export class TuhiUser {
    id!: string // id is mapped from cpn
    sub!: string
    confidenceLevel!: number
    firstName!: string
    lastName!: string
    email?: string | null
    birthDate?: string | null
    role?: string | null
    status?: string | null
    profession?: string | null
    specialty?: string | null
    preferredPhone?: string | null
    preferredEmail?: string | null
    preferredAddress?: string | null
    subscription?: TuhiSubscriptionData | null

    static async buildFromDto(tuhiUser: TuhiUserDto): Promise<TuhiUser> {
        const user = new TuhiUser()

        user.id = tuhiUser.id
        user.sub = tuhiUser.sub
        user.confidenceLevel = tuhiUser.confidenceLevel
        user.firstName = tuhiUser.firstName
        user.lastName = tuhiUser.lastName
        user.email = tuhiUser.email
        user.birthDate = tuhiUser.birthDate
        // role/status are additive on TuhiUserDto (ACA-102). Read defensively
        // so this stays green before the generated model is regenerated.
        const roleAware = tuhiUser as unknown as {
            role?: string | null
            status?: string | null
            profession?: string | null
            specialty?: string | null
            preferredPhone?: string | null
            preferredEmail?: string | null
            preferredAddress?: string | null
        }
        user.role = roleAware.role ?? null
        user.status = roleAware.status ?? null
        user.profession = roleAware.profession ?? null
        user.specialty = roleAware.specialty ?? null
        user.preferredPhone = roleAware.preferredPhone ?? null
        user.preferredEmail = roleAware.preferredEmail ?? null
        user.preferredAddress = roleAware.preferredAddress ?? null

        const subscription = tuhiUser.subscription
            ? await tuhiUser.subscription
            : null
        user.subscription = subscription
            ? toSubscriptionData(subscription)
            : null

        return user
    }

    // Convert to plain object for Redux serialization
    toData(): TuhiUserData {
        return {
            id: this.id,
            sub: this.sub,
            confidenceLevel: this.confidenceLevel,
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            birthDate: this.birthDate,
            role: this.role,
            status: this.status,
            profession: this.profession,
            specialty: this.specialty,
            preferredPhone: this.preferredPhone,
            preferredEmail: this.preferredEmail,
            preferredAddress: this.preferredAddress,
            subscription: this.subscription
                ? toSubscriptionData(this.subscription)
                : null,
        }
    }

    // Create TuhiUser instance from plain data
    static fromData(data: TuhiUserData): TuhiUser {
        const user = new TuhiUser()
        user.id = data.id
        user.sub = data.sub
        user.confidenceLevel = data.confidenceLevel
        user.firstName = data.firstName
        user.lastName = data.lastName
        user.email = data.email
        user.birthDate = data.birthDate
        user.role = data.role
        user.status = data.status
        user.profession = data.profession
        user.specialty = data.specialty
        user.preferredPhone = data.preferredPhone
        user.preferredEmail = data.preferredEmail
        user.preferredAddress = data.preferredAddress
        user.subscription = data.subscription
        return user
    }

    getConsultsLeft(): number {
        // Same as the getConsultsLeft() helper below: no subscription is 0.
        const startCount = this.subscription?.monthlyConsultLimit ?? 0
        const currentCount = this.subscription?.consultsUsedThisMonth ?? 0

        return startCount - currentCount
    }

    isFreeSubscription(): boolean {
        return this.subscription?.type === 'FREE'
    }

    allowConsults(): boolean {
        return (
            !this.isFreeSubscription() ||
            (this.isFreeSubscription() && this.getConsultsLeft() > 0)
        )
    }
}

// Helper functions for working with TuhiUserData in Redux
export const getConsultsLeft = (userData: TuhiUserData): number => {
    const startCount = userData.subscription?.monthlyConsultLimit ?? 0
    const currentCount = userData.subscription?.consultsUsedThisMonth ?? 0
    return startCount - currentCount
}

export const isFreeSubscription = (userData: TuhiUserData): boolean => {
    return userData.subscription?.type === 'FREE'
}

export const allowConsults = (userData: TuhiUserData): boolean => {
    return (
        !isFreeSubscription(userData) ||
        (isFreeSubscription(userData) && getConsultsLeft(userData) > 0)
    )
}

/**
 * Narrow the Redux `currentUser` to a signed-in user, or throw.
 *
 * `auth.currentUser` is `null` when signed out, but screens behind the
 * authenticated routes read it as always set. Reading `.id` on `null` used to
 * throw a TypeError at the read site; this throws at the same point with a
 * clear message instead, so callers keep the same control flow (their own
 * try/catch, or a rejected promise) while the type is honest.
 */
export const requireCurrentUser = (
    userData: TuhiUserData | null | undefined
): TuhiUserData => {
    if (!userData) {
        throw new Error('No signed-in user (auth.currentUser is not set)')
    }
    return userData
}

/**
 * The Tuhi user from a successful auth response, or throw.
 *
 * Auth responses type `tuhiUser` as optional because a failed response has
 * none. Reading `.toData()` on a missing user used to throw a TypeError at the
 * call site; this throws at the same point with a clear message instead.
 */
export const requireTuhiUser = (response: {
    tuhiUser?: TuhiUser
}): TuhiUser => {
    if (!response.tuhiUser) {
        throw new Error('Authentication response has no Tuhi user')
    }
    return response.tuhiUser
}
